'use client';

/**
 * Booking Inquiry Card
 *
 * Renders inside the message list when a message has
 * `metadata.intent_type === 'booking'`. Compact card surfacing the
 * five structured fields. Used for both sender and recipient.
 *
 * Phase 2 of:
 *   docs/wip/active/WIP_wiring_inbox_intent_booking_template_2026_05_13.md
 */
import { Briefcase } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { MessageWithSender } from '@kit/chat/types';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import type { BookingInquirySerialized } from './booking-inquiry-form';

interface BookingInquiryCardProps {
  message: MessageWithSender;
  isOwnMessage: boolean;
}

/**
 * Narrow guard. Returns the deserialized payload when present, or
 * `null` if the metadata shape is missing or malformed (so callers
 * can fall back to the standard bubble).
 */
export function readBookingPayload(
  metadata: unknown,
): BookingInquirySerialized | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const m = metadata as Record<string, unknown>;
  if (m.intent_type !== 'booking') return null;
  const p = m.booking_payload;
  if (!p || typeof p !== 'object') return null;
  const payload = p as Record<string, unknown>;
  const stringField = (key: string): string =>
    typeof payload[key] === 'string' ? (payload[key] as string) : '';
  return {
    subject: stringField('subject'),
    date_range_start: stringField('date_range_start'),
    date_range_end: stringField('date_range_end'),
    production: stringField('production'),
    compensation_range: stringField('compensation_range'),
    notes: stringField('notes'),
  };
}

export function BookingInquiryCard({
  message,
  isOwnMessage,
}: BookingInquiryCardProps) {
  const { t } = useTranslation();
  const payload = readBookingPayload(message.metadata);
  if (!payload) return null;

  return (
    <div className={cn('flex', isOwnMessage ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[min(420px,85%)] rounded-lg border p-3 shadow-sm',
          isOwnMessage
            ? 'bg-primary/5 border-primary/30'
            : 'bg-background border-foreground/15',
        )}
      >
        <div className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase">
          <Briefcase className="h-3.5 w-3.5" />
          <Trans i18nKey="chat:booking.card.title" />
        </div>

        <p className="text-sm leading-snug font-semibold">{payload.subject}</p>

        <dl className="mt-2 space-y-1 text-sm">
          {(payload.date_range_start || payload.date_range_end) && (
            <Row label={t('chat:booking.card.dates')}>
              {[payload.date_range_start, payload.date_range_end]
                .filter(Boolean)
                .join(' → ')}
            </Row>
          )}
          {payload.production && (
            <Row label={t('chat:booking.card.production')}>
              {payload.production}
            </Row>
          )}
          {payload.compensation_range && (
            <Row label={t('chat:booking.card.compensation')}>
              {payload.compensation_range}
            </Row>
          )}
        </dl>

        {payload.notes && (
          <p className="text-foreground/80 mt-2 text-sm whitespace-pre-wrap">
            {payload.notes}
          </p>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-1">
      <dt className="text-muted-foreground shrink-0">{label}:</dt>
      <dd className="text-foreground">{children}</dd>
    </div>
  );
}
